import { useState, useRef, KeyboardEvent, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { FlowNode } from '../types';
import { HttpStepConfig } from './config/HttpStepConfig';
import { HttpTransferStepConfig } from './config/HttpTransferStepConfig';
import { TokenLookupStepConfig } from './config/TokenLookupStepConfig';
import { TransformStepConfig } from './config/TransformStepConfig';
import { ForEachStepConfig } from './config/ForEachStepConfig';
import { SubFlowStepConfig } from './config/SubFlowStepConfig';
import { SwitchStepConfig } from './config/SwitchStepConfig';
import { LookupStepConfig } from './config/LookupStepConfig';
import { CryptoStepConfig } from './config/CryptoStepConfig';
import { CodeStepConfig } from './config/CodeStepConfig';
import { JsonPathTree } from './JsonPathTree';
import { TemplatePlayground } from './TemplatePlayground';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { stepDefinition, TONE_CLASSES } from './nodes/stepRegistry';
import {
  useExecutionView,
  buildStepOutputsMap,
  extractInboundFromSteps,
} from './ExecutionContext';

const EVAL_OPERATORS = [
  { value: 'eq', label: '== equals' },
  { value: 'neq', label: '!= not equals' },
  { value: 'contains', label: 'contains' },
  { value: 'empty', label: 'is empty' },
  { value: 'not_empty', label: 'is not empty' },
  { value: 'gt', label: '> greater than' },
  { value: 'gte', label: '>= greater or equal' },
  { value: 'lt', label: '< less than' },
  { value: 'lte', label: '<= less or equal' },
];
const OPERATORS_WITHOUT_VALUE = new Set(['empty', 'not_empty']);

interface EvalCondition {
  expression: string;
  operator: string;
  value: string;
}

interface EvalConfig {
  logic: 'and' | 'or';
  conditions: EvalCondition[];
}

interface Props {
  node: FlowNode;
  onUpdate: (updated: FlowNode) => void;
  onClose: () => void;
  onDelete: () => void;
  onOpenReference?: (section?: string) => void;
  aliasByNodeId?: Record<string, string>;
  initialTab?: TabKey;
}

type TabKey = 'config' | 'input' | 'output' | 'try';

const INPUT_CLASS = 'w-full border border-input rounded-md p-1.5 text-xs bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const INPUT_MONO_CLASS = `${INPUT_CLASS} font-mono`;
const TEXTAREA_CLASS = 'w-full border border-input rounded-md p-2 text-sm bg-background text-foreground resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function parseEvalLabel(config: Record<string, unknown> | undefined): string {
  return (config?.label as string) ?? '';
}

function parseEvalConfig(config: Record<string, unknown> | undefined): EvalConfig {
  if (Array.isArray(config?.conditions)) {
    return {
      logic: (config?.logic as 'and' | 'or') ?? 'and',
      conditions: (config?.conditions as EvalCondition[]).map(c => ({
        expression: c.expression ?? '',
        operator: c.operator ?? 'eq',
        value: c.value ?? '',
      })),
    };
  }
  return {
    logic: 'and',
    conditions: [{
      expression: (config?.expression as string) ?? '',
      operator: (config?.operator as string) ?? 'eq',
      value: (config?.value as string) ?? '',
    }],
  };
}

const EMPTY_CONDITION: EvalCondition = { expression: '', operator: 'eq', value: '' };

export function NodeConfigPanel({ node, onUpdate, onClose, onDelete, onOpenReference, aliasByNodeId, initialTab }: Props) {
  const [configText, setConfigText] = useState(
    node.config ? JSON.stringify(node.config, null, 2) : '{}'
  );
  const [evalCfg, setEvalCfg] = useState<EvalConfig>(() => parseEvalConfig(node.config));
  const [evalLabel, setEvalLabel] = useState<string>(() => parseEvalLabel(node.config));
  const [evalAlias, setEvalAlias] = useState<string>(() => (node.config?.alias as string) ?? '');
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(600);
  const step = stepDefinition(node.type);
  const StepIcon = step?.icon;
  const execView = useExecutionView();
  const hasExecution = execView.primary !== null;
  const compareEnabled = execView.compare !== null;
  const [tab, setTab] = useState<TabKey>(initialTab ?? 'config');

  useEffect(() => {
    setConfigText(node.config ? JSON.stringify(node.config, null, 2) : '{}');
    setEvalCfg(parseEvalConfig(node.config));
    setEvalLabel(parseEvalLabel(node.config));
    setEvalAlias((node.config?.alias as string) ?? '');
    setError(null);
    setTab(initialTab ?? 'config');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id, initialTab]);
  const dragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  useEffect(() => {
    if (configText === (node.config ? JSON.stringify(node.config, null, 2) : '{}')) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(configText);
      setError(null);
    } catch {
      setError('Invalid JSON');
      return;
    }
    if (JSON.stringify(parsed) === JSON.stringify(node.config ?? {})) return;
    const t = setTimeout(() => onUpdate({ ...node, config: parsed as Record<string, unknown> }), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configText]);

  useEffect(() => {
    const next = {
      ...(evalLabel ? { label: evalLabel } : {}),
      ...(evalAlias ? { alias: evalAlias } : {}),
      logic: evalCfg.logic,
      conditions: evalCfg.conditions.map(c => ({
        expression: c.expression,
        operator: c.operator,
        value: OPERATORS_WITHOUT_VALUE.has(c.operator) ? '' : c.value,
      })),
    };
    if (node.type !== 'eval') return;
    if (JSON.stringify(next) === JSON.stringify(node.config ?? {})) return;
    const t = setTimeout(() => onUpdate({ ...node, config: next }), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evalLabel, evalAlias, evalCfg]);

  function handleFormat() {
    try {
      setConfigText(JSON.stringify(JSON.parse(configText), null, 2));
      setError(null);
    } catch {
      setError('Invalid JSON');
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Tab') {
      e.preventDefault();
      const el = e.currentTarget;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = configText.slice(0, start) + '  ' + configText.slice(end);
      setConfigText(next);
      requestAnimationFrame(() => {
        el.selectionStart = start + 2;
        el.selectionEnd = start + 2;
      });
    }
  }

  function onDragMouseDown(e: React.MouseEvent) {
    e.preventDefault();
    dragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = width;

    function onMouseMove(ev: MouseEvent) {
      if (!dragging.current) return;
      const delta = dragStartX.current - ev.clientX;
      setWidth(Math.max(240, dragStartWidth.current + delta));
    }
    function onMouseUp() {
      dragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    }
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  return (
    <div
      className="absolute right-0 top-0 h-full bg-card text-card-foreground border-l border-border shadow-xl z-10 flex flex-col"
      style={{ width }}
    >
      <div
        onMouseDown={onDragMouseDown}
        className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 transition-colors"
      />

      <div className="flex items-center gap-2.5 border-b border-border px-4 py-3 mb-3">
        <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', TONE_CLASSES[step?.tone ?? 'slate'].tile)}>
          {StepIcon && <StepIcon className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold">{step?.label ?? node.type}</h2>
          <p className="truncate font-mono text-[11px] text-muted-foreground" title={node.id}>{node.id}</p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {hasExecution && (
        <div className="flex gap-0 border-b border-border px-4 text-xs">
          {(['config', 'input', 'output', 'try'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'px-3 py-1.5 -mb-px border-b-2 transition-colors',
                tab === t
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {t === 'config' ? 'Config' : t === 'input' ? 'Input' : t === 'output' ? 'Output' : 'Try'}
            </button>
          ))}
        </div>
      )}

      {hasExecution && tab !== 'config' ? (
        <ExecutionTabContent
          tab={tab}
          node={node}
          aliasByNodeId={aliasByNodeId ?? {}}
          width={width}
        />
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden px-4 pt-3">
          {node.type === 'webhook' ? (
            <p className="text-xs text-muted-foreground">
              Entry point. No configuration needed. Receives inbound webhook payload.
            </p>
          ) : node.type === 'retry' ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Stops the current execution and schedules a full retry of the flow with the
                same inbound payload.
              </p>
              <div className="text-xs rounded-md border border-warning/30 p-2 space-y-1 bg-warning/10">
                <div className="font-medium text-warning">Retry schedule</div>
                {[1,2,3,4,5].map(n => (
                  <div key={n} className="text-warning font-mono">
                    Retry {n}: {Math.pow(2, n)}s delay
                  </div>
                ))}
                <div className="text-warning">Retry 6: fails permanently</div>
              </div>
              <p className="text-xs text-muted-foreground">
                No output handle — this step always ends the current execution path.
              </p>
            </div>
          ) : node.type === 'fail' ? (
            <>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  Error message
                </label>
                <input
                  type="text"
                  className={INPUT_CLASS}
                  placeholder="No task ID returned for {{body.issueKey}}"
                  value={(node.config?.message as string) ?? ''}
                  onChange={e => onUpdate({ ...node, config: { ...node.config, message: e.target.value } })}
                />
              </div>
              <div className="mt-2">
                <label className="text-xs text-muted-foreground block mb-1">
                  Error code (optional)
                </label>
                <input
                  type="text"
                  className={INPUT_CLASS}
                  placeholder="HQ_NO_TASK_ID"
                  value={(node.config?.code as string) ?? ''}
                  onChange={e => onUpdate({ ...node, config: { ...node.config, code: e.target.value } })}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Fails the execution with this message (prefixed with [code] if set). Both fields
                support templates. No output handle — this step always ends the execution.
              </p>
            </>
          ) : node.type === 'wait' ? (
            <>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  Wait duration (seconds)
                </label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  className={INPUT_CLASS}
                  value={(node.config?.seconds as number) ?? 0}
                  onChange={e => {
                    const seconds = Math.max(0, Number(e.target.value));
                    onUpdate({ ...node, config: { ...node.config, seconds } });
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Pauses the execution for the specified number of seconds before continuing to the next step.
              </p>
            </>
          ) : node.type === 'comment' ? (
            <>
              <label className="text-xs text-muted-foreground block mb-1">
                Comment text
              </label>
              <textarea
                className="flex-1 border border-input rounded-md p-2 text-sm resize-y min-h-[160px] bg-warning/5 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Document the flow or an area here…"
                value={(node.config?.text as string) ?? ''}
                onChange={e => onUpdate({ ...node, config: { ...node.config, text: e.target.value } })}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Purely documentary. This comment has no connection points and is ignored
                during execution.
              </p>
            </>
          ) : node.type === 'eval' ? (
            <>
              <div className="mb-3">
                <label className="text-xs text-muted-foreground block mb-1">Label</label>
                <input
                  type="text"
                  className={INPUT_CLASS}
                  placeholder="e.g. HTTP Check"
                  value={evalLabel}
                  onChange={e => setEvalLabel(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="text-xs text-muted-foreground block mb-1">Step alias</label>
                <input
                  type="text"
                  className={INPUT_MONO_CLASS}
                  placeholder="e.g. status-check"
                  value={evalAlias}
                  onChange={e => setEvalAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                />
                {evalAlias && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Reference as{' '}
                    <span className="font-mono text-primary">{`{{${evalAlias}.result}}`}</span>
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-muted-foreground">Match</span>
                <div className="flex rounded-md border border-input overflow-hidden text-xs">
                  {(['and', 'or'] as const).map(l => (
                    <button
                      key={l}
                      onClick={() => setEvalCfg(c => ({ ...c, logic: l }))}
                      className={cn(
                        'px-3 py-1 font-medium transition-colors',
                        evalCfg.logic === l
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">conditions</span>
              </div>

              <div className="space-y-3 overflow-y-auto flex-1">
                {evalCfg.conditions.map((cond, i) => (
                  <div key={i} className="border border-border rounded-md p-2 space-y-2 relative">
                    {evalCfg.conditions.length > 1 && (
                      <button
                        onClick={() => setEvalCfg(c => ({ ...c, conditions: c.conditions.filter((_, j) => j !== i) }))}
                        className="absolute top-1.5 right-1.5 text-muted-foreground hover:text-destructive"
                        title="Remove condition"
                        aria-label="Remove condition"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <input
                      type="text"
                      className={INPUT_MONO_CLASS}
                      placeholder="{{http-1.status}}"
                      value={cond.expression}
                      onChange={e => setEvalCfg(c => ({
                        ...c,
                        conditions: c.conditions.map((cc, j) => j === i ? { ...cc, expression: e.target.value } : cc),
                      }))}
                    />
                    <select
                      className={INPUT_CLASS}
                      value={cond.operator}
                      onChange={e => setEvalCfg(c => ({
                        ...c,
                        conditions: c.conditions.map((cc, j) => j === i ? { ...cc, operator: e.target.value } : cc),
                      }))}
                    >
                      {EVAL_OPERATORS.map(op => (
                        <option key={op.value} value={op.value}>{op.label}</option>
                      ))}
                    </select>
                    {!OPERATORS_WITHOUT_VALUE.has(cond.operator) && (
                      <input
                        type="text"
                        className={INPUT_MONO_CLASS}
                        placeholder="value"
                        value={cond.value}
                        onChange={e => setEvalCfg(c => ({
                          ...c,
                          conditions: c.conditions.map((cc, j) => j === i ? { ...cc, value: e.target.value } : cc),
                        }))}
                      />
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={() => setEvalCfg(c => ({ ...c, conditions: [...c.conditions, { ...EMPTY_CONDITION }] }))}
                className="mt-2 w-full border border-input text-xs text-muted-foreground rounded-md py-1 hover:bg-accent hover:text-accent-foreground"
              >
                + Add condition
              </button>

              <p className="text-xs text-muted-foreground mt-2">
                Connect <span className="text-success font-medium">true</span> and{' '}
                <span className="text-destructive font-medium">false</span> handles to next steps.
              </p>
            </>
          ) : node.type === 'http' ? (
            <HttpStepConfig node={node} onUpdate={onUpdate} />
          ) : node.type === 'http-transfer' ? (
            <HttpTransferStepConfig node={node} onUpdate={onUpdate} />
          ) : node.type === 'transform' ? (
            <TransformStepConfig node={node} onUpdate={onUpdate} />
          ) : node.type === 'token-lookup' ? (
            <TokenLookupStepConfig node={node} onUpdate={onUpdate} />
          ) : node.type === 'foreach' ? (
            <ForEachStepConfig node={node} onUpdate={onUpdate} />
          ) : node.type === 'subflow' ? (
            <SubFlowStepConfig node={node} onUpdate={onUpdate} />
          ) : node.type === 'switch' ? (
            <SwitchStepConfig node={node} onUpdate={onUpdate} />
          ) : node.type === 'lookup' ? (
            <LookupStepConfig node={node} onUpdate={onUpdate} />
          ) : node.type === 'crypto' ? (
            <CryptoStepConfig node={node} onUpdate={onUpdate} />
          ) : node.type === 'code' ? (
            <CodeStepConfig node={node} onUpdate={onUpdate} />
          ) : node.type === 'join' ? (
            <p className="text-xs text-muted-foreground">
              Synchronisation point. Waits for all parallel branches to complete before continuing.
              No configuration needed.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-muted-foreground">Config (JSON)</label>
                <button
                  onClick={handleFormat}
                  className="text-xs text-primary hover:underline"
                >
                  Format
                </button>
              </div>
              <textarea
                className={cn(TEXTAREA_CLASS, 'flex-1 text-xs font-mono min-h-[120px]')}
                value={configText}
                onChange={e => setConfigText(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {error && <p className="text-destructive text-xs mt-1">{error}</p>}
            </>
          )}
        </div>
      )}

      {node.type !== 'comment' && tab === 'config' && (
        <div className="mt-4 pt-4 border-t border-border px-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-muted-foreground font-medium">Template variables</p>
            {onOpenReference && (
              <button
                onClick={() => onOpenReference()}
                className="text-xs text-primary hover:underline"
                title="All sources, pipes and functions with examples"
              >
                Open reference →
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {`{{body.field}}, {{headers.x-id}}, {{query.page}},`}<br/>
            {`{{env.MY_KEY}}, {{${(node.config?.alias as string)?.trim() || node.id}.field}}`}
          </p>
        </div>
      )}

      {tab === 'config' && (
        <div className="mt-4 pt-4 border-t border-border px-4 pb-4">
          <Button
            variant="outline"
            className="w-full text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
            onClick={onDelete}
          >
            Delete step
          </Button>
        </div>
      )}
      {compareEnabled && tab !== 'config' && (
        <div className="px-4 py-2 text-[10px] text-tone-violet border-t border-border">
          Compare mode is active. Use the picker header to clear.
        </div>
      )}
    </div>
  );
}

interface ExecutionTabContentProps {
  tab: TabKey;
  node: FlowNode;
  aliasByNodeId: Record<string, string>;
  width: number;
}

function ExecutionTabContent({ tab, node, aliasByNodeId }: ExecutionTabContentProps) {
  const { primary, compare } = useExecutionView();

  const inbound = useMemo(
    () => primary ? extractInboundFromSteps(primary.execution, primary.steps) : { body: '{}', headers: {}, query: {} },
    [primary],
  );

  const stepOutputs = useMemo(
    () => primary ? buildStepOutputsMap(primary.steps, aliasByNodeId) : {},
    [primary, aliasByNodeId],
  );

  const tryContext = useMemo(() => ({
    body: inbound.body,
    headers: inbound.headers,
    query: inbound.query,
    env: {} as Record<string, string>,
    batch: [] as string[],
    stepOutputs,
  }), [inbound, stepOutputs]);

  if (!primary) return null;

  const step = primary.stepByNodeId.get(node.id) ?? null;
  const compareStep = compare?.stepByNodeId.get(node.id) ?? null;

  const isWebhook = node.type === 'webhook';
  const inputJson = isWebhook ? primary.execution.inboundPayload : (step?.input ?? '{}');
  const outputJson = isWebhook ? primary.execution.inboundPayload : (step?.output ?? null);
  const compareInputJson = isWebhook
    ? compare?.execution.inboundPayload ?? null
    : (compareStep?.input ?? null);
  const compareOutputJson = isWebhook
    ? compare?.execution.inboundPayload ?? null
    : (compareStep?.output ?? null);

  const pathPrefix = isWebhook ? 'body' : node.id;

  if (tab === 'input') {
    if (!isWebhook && !step) {
      return (
        <div className="flex-1 px-4 pt-3 text-xs text-muted-foreground">
          This step did not run in the selected execution.
        </div>
      );
    }
    return (
      <div className="flex-1 overflow-y-auto px-4 pt-3">
        <p className="text-xs text-muted-foreground mb-2">
          {isWebhook ? 'Inbound webhook payload' : 'Context the engine saw before this step ran'}
        </p>
        <div className={compare ? 'grid grid-cols-2 gap-2' : ''}>
          <JsonPathTree json={inputJson} pathPrefix={pathPrefix} />
          {compare && compareInputJson !== null && (
            <JsonPathTree json={compareInputJson} pathPrefix={pathPrefix} />
          )}
        </div>
      </div>
    );
  }

  if (tab === 'output') {
    if (isWebhook) {
      return (
        <div className="flex-1 overflow-y-auto px-4 pt-3">
          <p className="text-xs text-muted-foreground mb-2">Webhook has no separate output — the inbound payload is the body.</p>
          <JsonPathTree json={primary.execution.inboundPayload} pathPrefix="body" />
        </div>
      );
    }
    if (!step) {
      return (
        <div className="flex-1 px-4 pt-3 text-xs text-muted-foreground">
          This step did not run in the selected execution.
        </div>
      );
    }
    return (
      <div className="flex-1 overflow-y-auto px-4 pt-3 space-y-2">
        {step.error && (
          <div className="p-2 bg-destructive/10 rounded-md text-xs text-destructive font-mono border border-destructive/30">
            {step.error}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Status: <span className="font-medium text-foreground">{step.status}</span> · {step.durationMs}ms
        </p>
        {outputJson ? (
          <div className={compare ? 'grid grid-cols-2 gap-2' : ''}>
            <JsonPathTree json={outputJson} pathPrefix={node.id} />
            {compare && compareOutputJson && (
              <JsonPathTree json={compareOutputJson} pathPrefix={node.id} />
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No output recorded.</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 pt-3">
      <TemplatePlayground
        contextRequest={tryContext}
        contextHint={isWebhook
          ? `Context: inbound payload of execution ${primary.execution.id.slice(0, 8)}`
          : `Context: input of step ${node.id}` + (step ? '' : ' (step did not run; using inbound only)')}
      />
    </div>
  );
}
